import { motion } from 'framer-motion';
import { format } from 'date-fns';
import {
  Shield,
  Lock,
  Unlock,
  Globe,
  Clock,
  AlertTriangle,
  Bot,
  MapPin,
  Hash,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';

interface SecurityEvent {
  id: string;
  qr_code_id: string;
  event_type: string;
  ip_address: string | null;
  country: string | null;
  city: string | null;
  is_blocked: boolean;
  event_data: unknown;
  created_at: string;
}

interface SecurityEventsListProps {
  events: SecurityEvent[];
  loading?: boolean;
}

const EVENT_ICONS: Record<string, React.ReactNode> = {
  password_success: <Unlock className="h-4 w-4 text-green-500" />,
  password_failed: <Lock className="h-4 w-4 text-red-500" />,
  password_attempt_locked: <AlertTriangle className="h-4 w-4 text-amber-500" />,
  expired_access: <Clock className="h-4 w-4 text-muted-foreground" />,
  scan_limit_reached: <Hash className="h-4 w-4 text-blue-500" />,
  unique_scan_limit: <Hash className="h-4 w-4 text-blue-500" />,
  geo_blocked: <Globe className="h-4 w-4 text-orange-500" />,
  ip_blocked: <Shield className="h-4 w-4 text-red-500" />,
  bot_detected: <Bot className="h-4 w-4 text-purple-500" />,
};

const EVENT_LABELS: Record<string, string> = {
  password_success: 'Password Verified',
  password_failed: 'Password Failed',
  password_attempt_locked: 'Access Locked',
  expired_access: 'Expired Access Attempt',
  scan_limit_reached: 'Scan Limit Reached',
  unique_scan_limit: 'Device Limit Reached',
  geo_blocked: 'Geo Blocked',
  ip_blocked: 'IP Blocked',
  bot_detected: 'Bot Detected',
};

export function SecurityEventsList({ events, loading }: SecurityEventsListProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Security Events</CardTitle>
          <CardDescription>Loading events...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (events.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Security Events</CardTitle>
          <CardDescription>No security events recorded</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12 text-muted-foreground">
            <Shield className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p>No security events have been recorded yet.</p>
            <p className="text-sm mt-2">
              Events will appear here when users interact with protected QR codes.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Security Events</CardTitle>
        <CardDescription>
          Showing the last {events.length} security events
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[500px] pr-4">
          <div className="space-y-2">
            {events.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.02 }}
                className={`p-3 rounded-lg border ${
                  event.is_blocked ? 'bg-destructive/5 border-destructive/20' : 'bg-muted/50'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      {EVENT_ICONS[event.event_type] || (
                        <Shield className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">
                          {EVENT_LABELS[event.event_type] || event.event_type}
                        </span>
                        {event.is_blocked && (
                          <Badge variant="destructive" className="text-xs">
                            Blocked
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        {event.ip_address && (
                          <span className="font-mono">{event.ip_address}</span>
                        )}
                        {event.country && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {event.city ? `${event.city}, ` : ''}
                            {event.country}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {format(new Date(event.created_at), 'MMM d, HH:mm')}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
